import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type ClientFilterState = {
  searchTerm: string;
  hasChatwoot: boolean | null; // null = todos, true = vinculado, false = não vinculado
  ageRange: string; // "" = todos, "0-18", "19-30", "31-50", "51-70", "70+", "sem-data"
};

type ClientFiltersProps = {
  filters: ClientFilterState;
  onFiltersChange: (filters: ClientFilterState) => void;
  hasChatwootIntegration?: boolean;
  counts?: {
    hasChatwoot: {
      vinculado: number;
      naoVinculado: number;
    };
    ageRanges: Record<string, number>;
  };
};

type TabType = "resumo" | "chatwoot" | "idade";

export function ClientFilters({
  filters,
  onFiltersChange,
  hasChatwootIntegration = false,
  counts,
}: ClientFiltersProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("resumo");

  const filterCount = 
    (hasChatwootIntegration && filters.hasChatwoot !== null ? 1 : 0) +
    (filters.ageRange ? 1 : 0);

  const clearAllFilters = () => {
    onFiltersChange({
      searchTerm: filters.searchTerm,
      hasChatwoot: null,
      ageRange: "",
    });
  };

  const removeFilter = (type: keyof ClientFilterState) => {
    if (type === "hasChatwoot") {
      onFiltersChange({ ...filters, hasChatwoot: null });
    } else if (type === "ageRange") {
      onFiltersChange({ ...filters, ageRange: "" });
    }
  };

  const tabs: Array<{ id: TabType; label: string; icon: string }> = [
    { id: "resumo", label: "Resumo", icon: "📋" },
    { id: "idade", label: "Idade", icon: "🎂" },
    ...(hasChatwootIntegration ? [{ id: "chatwoot" as TabType, label: "Chatwoot", icon: "💬" }] : []),
  ];

  const getTabCount = (tabId: TabType) => {
    switch (tabId) {
      case "chatwoot": return filters.hasChatwoot !== null ? 1 : 0;
      case "idade": return filters.ageRange ? 1 : 0;
      default: return 0;
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <Button variant="outline" className="relative">
                <Filter className="h-4 w-4 mr-2" />
                Filtros
                {filterCount > 0 && (
                  <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center" variant="default">
                    {filterCount}
                  </Badge>
                )}
              </Button>
            </TooltipTrigger>
          </PopoverTrigger>
          <PopoverContent className="w-[500px] p-0" align="start">
            <div className="flex h-[450px]">
              {/* Sidebar */}
              <div className="w-48 border-r bg-muted/30 p-2 overflow-y-auto">
                {tabs.map((tab) => {
                  const count = getTabCount(tab.id);
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={cn(
                        "w-full flex items-center justify-between px-3 py-2 rounded text-sm mb-1",
                        activeTab === tab.id
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-muted"
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <span>{tab.icon}</span>
                        <span>{tab.label}</span>
                      </span>
                      {count > 0 && (
                        <Badge variant={activeTab === tab.id ? "secondary" : "default"} className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                          {count}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Content */}
              <div className="flex-1 p-4 overflow-y-auto">
                {activeTab === "resumo" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">Resumo dos Filtros</h3>
                      {filterCount > 0 && (
                        <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                          Limpar tudo
                        </Button>
                      )}
                    </div>

                    {filterCount === 0 ? (
                      <p className="text-muted-foreground text-sm">Nenhum filtro aplicado</p>
                    ) : (
                      <div className="space-y-3">
                        {filters.ageRange && (
                          <div>
                            <Label className="text-xs text-muted-foreground">Faixa Etária</Label>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <Badge variant="secondary" className="gap-1">
                                {filters.ageRange === "0-18" && "0-18 anos"}
                                {filters.ageRange === "19-30" && "19-30 anos"}
                                {filters.ageRange === "31-50" && "31-50 anos"}
                                {filters.ageRange === "51-70" && "51-70 anos"}
                                {filters.ageRange === "70+" && "Acima de 70 anos"}
                                {filters.ageRange === "sem-data" && "Sem data de nascimento"}
                                <X
                                  className="h-3 w-3 cursor-pointer"
                                  onClick={() => removeFilter("ageRange")}
                                />
                              </Badge>
                            </div>
                          </div>
                        )}
                        {hasChatwootIntegration && filters.hasChatwoot !== null && (
                          <div>
                            <Label className="text-xs text-muted-foreground">Chatwoot</Label>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <Badge variant="secondary" className="gap-1">
                                {filters.hasChatwoot ? "Vinculado" : "Não vinculado"}
                                <X
                                  className="h-3 w-3 cursor-pointer"
                                  onClick={() => removeFilter("hasChatwoot")}
                                />
                              </Badge>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "idade" && (
                  <div className="space-y-4">
                    <h3 className="font-semibold">Faixa Etária</h3>
                    <RadioGroup value={filters.ageRange} onValueChange={(value) => onFiltersChange({ ...filters, ageRange: value })}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="" id="age-all" />
                        <Label htmlFor="age-all" className="flex-1 flex items-center gap-2">
                          Todas as idades
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.ageRanges ? Object.values(counts.ageRanges).reduce((a, b) => a + b, 0) : 0}
                          </Badge>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="0-18" id="age-0-18" />
                        <Label htmlFor="age-0-18" className="flex-1 flex items-center gap-2">
                          0-18 anos
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.ageRanges?.["0-18"] ?? 0}
                          </Badge>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="19-30" id="age-19-30" />
                        <Label htmlFor="age-19-30" className="flex-1 flex items-center gap-2">
                          19-30 anos
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.ageRanges?.["19-30"] ?? 0}
                          </Badge>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="31-50" id="age-31-50" />
                        <Label htmlFor="age-31-50" className="flex-1 flex items-center gap-2">
                          31-50 anos
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.ageRanges?.["31-50"] ?? 0}
                          </Badge>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="51-70" id="age-51-70" />
                        <Label htmlFor="age-51-70" className="flex-1 flex items-center gap-2">
                          51-70 anos
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.ageRanges?.["51-70"] ?? 0}
                          </Badge>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="70+" id="age-70-plus" />
                        <Label htmlFor="age-70-plus" className="flex-1 flex items-center gap-2">
                          Acima de 70 anos
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.ageRanges?.["70+"] ?? 0}
                          </Badge>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="sem-data" id="age-no-data" />
                        <Label htmlFor="age-no-data" className="flex-1 flex items-center gap-2">
                          Sem data de nascimento
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.ageRanges?.["sem-data"] ?? 0}
                          </Badge>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}

                {activeTab === "chatwoot" && (
                  <div className="space-y-4">
                    <h3 className="font-semibold">Status Chatwoot</h3>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="chatwoot-vinculado"
                          checked={filters.hasChatwoot === true}
                          onCheckedChange={(checked) => {
                            onFiltersChange({
                              ...filters,
                              hasChatwoot: checked ? true : null,
                            });
                          }}
                        />
                        <Label htmlFor="chatwoot-vinculado" className="flex-1 flex items-center gap-2">
                          Vinculado
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.hasChatwoot?.vinculado ?? 0}
                          </Badge>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="chatwoot-nao-vinculado"
                          checked={filters.hasChatwoot === false}
                          onCheckedChange={(checked) => {
                            onFiltersChange({
                              ...filters,
                              hasChatwoot: checked ? false : null,
                            });
                          }}
                        />
                        <Label htmlFor="chatwoot-nao-vinculado" className="flex-1 flex items-center gap-2">
                          Não vinculado
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.hasChatwoot?.naoVinculado ?? 0}
                          </Badge>
                        </Label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
        <TooltipContent>
          <p>Filtre a listagem de clientes</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

